import {Component, Input, OnInit, AfterViewInit, HostBinding, OnDestroy, ViewChild, ElementRef} from "@angular/core";
import {User} from "../../../../user/user";
import {UserSubscription, UserSubscriptionCategory, UserSubscriptionType} from "../../../../user/user-subscription";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../store";
import {CookieService} from "ngx-cookie";
import {ChosenPlan} from "../../payment-info/models/plan.model";
import {GoogleAnalyticsService} from "../../../services/google-analytics/google-analytics.service";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {CommonModule} from "@angular/common";
import SecondaryFeatures from './secondaryFeatures.json';
import {DbNumberFormatPipe} from "../../../pipes/db-number-ceil.pipe";
import {
  PricingPeriodSwitcherComponent
} from '../../../../workspace/pricing/plan-period-switcher/pricing-period-switcher.component';
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {CountPipe} from "../../../pipes/count.pipe";
import {SizePipe} from "../../../pipes/size.pipe";
import {PaymentDialogComponent} from "../../../../workspace/billing/payment-dialog/payment-dialog.component";
import {DialogService} from "../../../../../ng2-bootstrap-modal/dialog.service";
import {DbModalService} from "../../../../modules/modal/modal";
import {DbActiveModal} from "../../../../modules/modal/modal-ref";
import {RouterModule} from "@angular/router";
import {getCurrencySymbol, isCurrencySymbol} from "../../../utils/currency-symbols";
import {Subject} from "rxjs";
import {DbNumberParseFloatPipe} from "../../../pipes/db-number-paresFloat.pipe";
import {DbTextPipe} from "../../../../shared/pipes/db-text.pipe";
import {ResellerCidService} from "../../../services/reseller-cid/reseller-cid.service";

@Component({
    selector: 'db-api-reseller-pro',
    templateUrl: './api-reseller-pro.component.html',
    styleUrls: ['./api-reseller-pro.component.scss'],
    imports: [
        CommonModule,
        DbNumberFormatPipe,
        PricingPeriodSwitcherComponent,
        FormsModule,
        ReactiveFormsModule,
        DbNumberParseFloatPipe,
        RouterModule,
        DbTextPipe
    ]
})
export class ApiResellerProComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() user: User;
  @Input() currentSubscription: UserSubscription;
  @Input() demoUser?: boolean = false;
  @Input() fromDialog?: boolean = false;

  @HostBinding('class.from-dialog')
  get isFromDialog() {
    return this.fromDialog;
  }
  @ViewChild('plansContainer') plansContainer: ElementRef;
  currentSlide: number = 0;
  totalSlides: number = 1;
  showLeftGradient: boolean = false;
  showRightGradient: boolean = false;
  isMobile = window.innerWidth <= 991;
  secondaryFeatures: any = SecondaryFeatures;
  chosenPlan: ChosenPlan;
  couponCode: string;
  expirationDate: string;
  subscriptions: any[] = [];
  userHostingSubscriptions: UserSubscription[];
  upgradeableSubscription: UserSubscription[];
  UserSubscriptionCategory = UserSubscriptionCategory;
  UserSubscriptionType = UserSubscriptionType;
  planTypeInfo: any[];
  isTier1Country: boolean;
  periodSwitch: number;
  periodType: string;
  showPeriodSwitcher: boolean = false;
  currencySymbol: string = '$';
  isCurrencyBeforeAmount: boolean = true;
  companyName: string = 'Dashboard';
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  private _computedScrollStep: number = 344;
  private readonly _onResize = () => this._updateTotalSlides();

  constructor(
    private _store: Store<fromStore.DbStoreState>,
    private _cookieService: CookieService,
    private _dialogService: DialogService,
    private _modalService: DbModalService,
    private _activeModal: DbActiveModal,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _resellerCidService: ResellerCidService
  ) {
  }

  ngAfterViewInit(): void {
    this._updateTotalSlides();
    window.addEventListener('resize', this._onResize);
  }

  ngOnInit() {
    if (!this.currentSubscription?.trialClient) {
      this.periodSwitch = this.currentSubscription?.plan?.periodNumber;
      this.periodType = this.periodSwitch === 1 ? 'm' : 'y';
    }
    else {
      this.periodSwitch = 1;
      this.periodType = 'm';
    }
    const getExperimentalCookie = this._cookieService.get('db-experiment');
    this.isTier1Country = getExperimentalCookie ?
      JSON.parse(getExperimentalCookie) && JSON.parse(getExperimentalCookie).t1PExp && JSON.parse(getExperimentalCookie).t1PExp === 'b' : false;
    this.getAvailableSubscriptions();
    this._getCompanyInfo();
  }

  private _getCompanyInfo(): void {
    this._store.select(fromStore.getCompanyInfoLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getCompanyInfo)),
        takeUntil(this._destroyStream$)
      )
      .subscribe((companyInfo: any) => {
        this.currencySymbol = getCurrencySymbol(companyInfo?.agency?.currency);
        this.isCurrencyBeforeAmount = isCurrencySymbol(this.currencySymbol);
        this.companyName = companyInfo?.branding?.company_name || companyInfo?.agency?.name || 'YourBrand';
      });
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this._onResize);
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }


  closeModal(event: any): void {
    console.log(event);
  }

  private getAvailableSubscriptions(): void {
    this._store.select(fromStore.getUserLoaded)
        .pipe(
            first(),
            switchMap(() => this._store.select(fromStore.getUserAvailableHostingSubscriptions())),
            first(res => !!res.length),
        )
        .subscribe(availableSubscriptions => {
            this.userHostingSubscriptions = availableSubscriptions;
            this.checkIfShouldShowPeriodSwitcher();
            this.determineUpgradeSubscriptions();
        });
  }


  private checkIfShouldShowPeriodSwitcher(): void {
    if (!this.userHostingSubscriptions || this.userHostingSubscriptions.length === 0) {
      this.showPeriodSwitcher = false;
      return;
    }

    const hasMonthlyPlans = this.userHostingSubscriptions.some(sub =>
        sub.plan?.periodType === 'm' || sub.plan?.periodNumber === 1
    );
    const hasYearlyPlans = this.userHostingSubscriptions.some(sub =>
        sub.plan?.periodType === 'y' || sub.plan?.periodNumber === 12);

    // Get current subscription period info
    const currentPeriodType = this.currentSubscription?.plan?.periodType;
    const currentPeriodNumber = this.currentSubscription?.plan?.periodNumber;

    // Determine opposite period
    const isCurrentMonthly = currentPeriodType === 'm' || currentPeriodNumber === 1;
    const oppositePeriodType = isCurrentMonthly ? 'y' : 'm';
    const oppositePeriodNumber = isCurrentMonthly ? 12 : 1;

    const coupleSubscription = this.upgradeableSubscription
        && this.currentSubscription?.plan?.periodNumber === 1
        && this.upgradeableSubscription.find(subscription => subscription.id === this.currentSubscription.id);

    // Check if current plan exists in available subscriptions
    const hasCurrentPlan = !this.currentSubscription?.trialClient
        && (this.periodSwitch === 1 || (this.periodSwitch === 12 && !coupleSubscription));

    // Check if opposite period plan exists in available subscriptions
    const hasOppositePlan = this.userHostingSubscriptions.some(sub =>
      sub.plan?.periodType === oppositePeriodType || sub.plan?.periodNumber === oppositePeriodNumber
    );

    // Show switcher only if both current and opposite period plans exist
    this.showPeriodSwitcher = hasCurrentPlan ? hasCurrentPlan && hasOppositePlan : hasMonthlyPlans && hasYearlyPlans;
  }

  private getSubscriptions() {
    this.subscriptions = [];
    const coupleSubscription = this.upgradeableSubscription
        && this.currentSubscription?.plan?.periodNumber === 1
        && this.upgradeableSubscription.find(subscription => subscription.id === this.currentSubscription.id);

    if (!this.currentSubscription?.trialClient
        && (this.periodSwitch === 1 || (this.periodSwitch === 12 && !coupleSubscription))) {
      this.subscriptions.push(this.currentSubscription);
    }
    if (this.periodSwitch === 12 && coupleSubscription && !this.currentSubscription?.trialClient) {
        const index = this.upgradeableSubscription.indexOf(coupleSubscription);
        if (index !== -1) {
            this.upgradeableSubscription.splice(index, 1);
        }
        this.subscriptions.push(coupleSubscription);
    }
    if (this.upgradeableSubscription) {
      this.subscriptions = [...this.subscriptions, ...this.upgradeableSubscription];

      this.subscriptions.forEach(subscription => {
        if (subscription.agencyFeatures) {
          if (!Array.isArray(subscription.agencyFeatures)) {
            subscription.agencyFeatures = Object.values(subscription.agencyFeatures);
          }
          subscription.agencyFeatures.sort((a, b) => {
            if (a.slug === 'tenweb_hosting') return -1;
            if (b.slug === 'tenweb_hosting') return 1;
            return 0;
          });

          const primarySlugs = ['tenweb_hosting', 'monthly_credit_limit'];
          const storageSlugs = ['ssd', 'visitors'];
          const excludeSlugs = new Set([...primarySlugs, ...storageSlugs, 'builder_type', 'monthly_generation_limit']);

          subscription.primaryFeatures = primarySlugs
            .map(slug => subscription.agencyFeatures.find((f: any) => f.slug === slug))
            .filter(Boolean);

          subscription.storageFeatures = storageSlugs
            .map(slug => subscription.agencyFeatures.find((f: any) => f.slug === slug))
            .filter(Boolean);

          subscription.additionalFeatures = subscription.agencyFeatures
            .filter((f: any) => !excludeSlugs.has(f.slug));
        }
      });
    }

    this.processSecondaryFeatures();
    this._updateTotalSlides();
  }

  private processSecondaryFeatures(): void {
    const countPipe = new CountPipe();
    const sizePipe = new SizePipe();
    this.secondaryFeatures = SecondaryFeatures;
    this.secondaryFeatures = this.secondaryFeatures.map((planGroup: any) => {
      const planType = Object.keys(planGroup)[0];
      const subscription = this.subscriptions.find(sub => sub.category === planType);

      if (!subscription) return planGroup;

      const plan = subscription;
      const features = planGroup[planType].map((feature: any) => {
        let title = feature.title;

        // Replace visits placeholder
        if (title.includes('%ai_generations%')) {
          const aiGenerations =
            '<b>' + plan.plan.aiBuilderService?.regeneration_count?.parameter_value + '</b>';
          title = title.replace('%ai_generations%', aiGenerations);
        }
        if (title.includes('%visits%')) {
          const visits = plan.plan.shortFeatures?.visits ||
            '<b>' + countPipe.transform(plan.plan.hostingService?.hosting_visits?.parameter_value) + '</b>' ||
            'not-found';
          title = title.replace('%visits%', visits);
        }

        // Replace storage placeholder
        if (title.includes('%storage%')) {
          const storage = plan.plan.shortFeatures?.storage ||
            '<b>' + sizePipe.transform(plan.plan.hostingService?.hosting_storage?.parameter_value * 1024 * 1024) + '</b>';
          title = title.replace('%storage%', storage);
        }

        return {
          ...feature,
          title
        };
      });

      return {
        [planType]: features
      };
    });

  }

  private determineUpgradeSubscriptions(): void {
      this.upgradeableSubscription = this.userHostingSubscriptions.filter((userSubscription: UserSubscription): boolean => {
        return userSubscription && userSubscription.plan &&
          userSubscription.plan.shortFeatures &&
          // userSubscription.subscriptionCategory === UserSubscriptionType.PLATFORM &&
          userSubscription.plan.periodType === this.periodType;
      });

      // Sort plans from lowest to highest price
      this.upgradeableSubscription.sort((a, b) => {
        const priceA = a.plan.planPrice / a.plan.periodNumber;
        const priceB = b.plan.planPrice / b.plan.periodNumber;
        return priceA - priceB;
      });

    // this.planTypeInfo = ApiResellerPlanInfoResolver.planInfoDesc(false, false, this.user, false, this.currentSubscription, this.upgradeableAPIStarterSubscription, this.upgradeableAPIPremiumSubscription, this.upgradeableAPIUltimateSubscription);
    this.getSubscriptions();
  }

  onPeriodSwitchChange(value: number): void {
    this.periodSwitch = value;
    this.periodType = value === 1 ? 'm' : 'y';
    this.currentSlide = 0;
    if (this.plansContainer?.nativeElement) {
      this.plansContainer.nativeElement.scrollLeft = 0;
    }
    this.determineUpgradeSubscriptions();
  }

  get totalSlidesArray(): number[] {
    return Array.from({ length: this.totalSlides }, (_, i) => i);
  }

  private _updateTotalSlides(): void {
    setTimeout(() => {
      const el = this.plansContainer?.nativeElement;
      if (!el) return;
      if (this.subscriptions?.length > 3) {
        const cards = el.querySelectorAll('.plan-item');
        if (cards.length >= 2) {
          this._computedScrollStep = (cards[1] as HTMLElement).offsetLeft - (cards[0] as HTMLElement).offsetLeft;
        } else if (cards.length === 1) {
          this._computedScrollStep = (cards[0] as HTMLElement).offsetWidth;
        }
        const maxScroll = el.scrollWidth - el.offsetWidth;
        this.totalSlides = maxScroll > 0 ? Math.round(maxScroll / this._computedScrollStep) + 1 : 1;
      } else {
        this.totalSlides = 1;
      }
      this._updateGradients(el);
    });
  }

  private _updateGradients(el: HTMLElement): void {
    this.showLeftGradient = el.scrollLeft > 2;
    this.showRightGradient = el.scrollLeft + el.offsetWidth < el.scrollWidth - 2;
  }

  onPlansScroll(): void {
    const el = this.plansContainer?.nativeElement;
    if (el) {
      this.currentSlide = Math.min(this.totalSlides - 1, Math.round(el.scrollLeft / this._computedScrollStep));
      this._updateGradients(el);
    }
  }

  slideLeft(): void {
    this.currentSlide = Math.max(0, this.currentSlide - 1);
    this.plansContainer?.nativeElement.scrollTo({ left: this.currentSlide * this._computedScrollStep, behavior: 'smooth' });
  }

  slideRight(): void {
    this.currentSlide = Math.min(this.totalSlides - 1, this.currentSlide + 1);
    this.plansContainer?.nativeElement.scrollTo({ left: this.currentSlide * this._computedScrollStep, behavior: 'smooth' });
  }

  slideTo(index: number): void {
    this.currentSlide = index;
    this.plansContainer?.nativeElement.scrollTo({ left: index * this._computedScrollStep, behavior: 'smooth' });
  }

  public chooseSubscription(id: number, extend: boolean | undefined = false): void {
    if (this.fromDialog) {
      this.close();
    }
    const gtag = window['gtag'];
    if (typeof gtag === 'function') {
      const resellerCid = this._resellerCidService.get();
      gtag("event", "upgrade_popup_open", {
          twdb_user_id: this.user && this.user.id || 0,
          ...(resellerCid ? { cid: resellerCid } : {}),
      });
    }

    const coupon = this.couponCode;
    this._dialogService
      .addDialog(PaymentDialogComponent, {
        upgradeSubscriptionId: id,
        fromDialog: false,
        couponCode: coupon,
        fromApiResellerPro: this.fromDialog
      });
  }

  public close(): void {
    this._activeModal.close();
  }
}
